import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import data from "../content/case-studies.json";
import { CLIENT_LOGOS } from "../content/clients";
import stats from "./content/site-stats.json";
import { WistiaEmbed } from "../components/WistiaEmbed";
import { LogoTile } from "../components/CaseCard";
import { draftPath } from "./DraftLayout";

export const caseBySlug = (slug) => data.cases.find((c) => c.slug === slug);

export function Shell({ children, tone = "white", testId, className = "" }) {
  const bg = { white: "bg-white", fog: "bg-fog", navy: "bg-navy" }[tone];
  return (
    <section data-testid={testId} className={`${bg} ${tone === "navy" ? "grain" : ""}`}>
      <div className={`mx-auto max-w-6xl px-5 py-20 md:py-28 ${className}`}>{children}</div>
    </section>
  );
}

export function H2({ children, light = false, className = "" }) {
  return <h2 className={`text-[28px] font-bold leading-[1.1] tracking-[-0.025em] md:text-[38px] ${light ? "text-white" : "text-navy"} ${className}`}>{children}</h2>;
}

export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const WALL = [
  "/assets/good-riddance/gr22.webp", "/assets/jamie-anne/ja-oil-review.webp", "/assets/clean-eez/grout-eez.webp",
  "/assets/good-riddance/gr42-poster.jpg", "/assets/jamie-anne/ja-roller-ugc.webp", "/assets/clean-eez/grouteez-ai-poster.jpg",
  "/assets/good-riddance/gr61.webp", "/assets/clean-eez/saveit-poster.jpg", "/assets/jamie-anne/ja-roller-static.webp",
  "/assets/good-riddance/gr56-poster.jpg", "/assets/jamie-anne/ja-video-poster.jpg", "/assets/clean-eez/grout-video-2-poster.jpg",
];

export function CreativeWall() {
  const cols = [WALL.slice(0, 4), WALL.slice(4, 8), WALL.slice(8, 12)];
  return (
    <div data-testid="creative-wall" className="relative h-[520px] overflow-hidden md:h-[640px]" aria-hidden="true">
      <div className="absolute inset-0 z-10 bg-[linear-gradient(to_bottom,#07005D_0%,rgba(7,0,93,0.7)_8%,transparent_22%,transparent_78%,rgba(7,0,93,0.7)_92%,#07005D_100%)]" />
      <div className="absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-navy to-transparent" />
      <div className="absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-navy to-transparent" />
      <div className="flex origin-center rotate-[-6deg] scale-[1.15] gap-4">
        {cols.map((col, i) => (
          <div key={i} className="flex w-1/3 flex-col gap-4" style={{ animation: `wall-scroll ${38 + i * 7}s linear infinite`, animationDirection: i === 1 ? "reverse" : "normal" }}>
            {[...col, ...col].map((src, j) => (
              <img key={j} src={src} alt="" loading="lazy" className="w-full rounded-2xl object-cover shadow-[0_8px_30px_rgba(0,0,0,0.35)] ring-1 ring-white/10" style={{ aspectRatio: "4 / 5" }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatRow({ items, dark = false, testId }) {
  const line = dark ? "divide-white/10 border-white/10" : "divide-mist border-mist";
  return (
    <div data-testid={testId} className={`grid divide-y border-y sm:grid-cols-3 sm:divide-x sm:divide-y-0 ${line}`}>
      {items.map((s, i) => (
        <Reveal key={i} delay={i * 0.08} className="py-7 sm:px-8 sm:first:pl-0 sm:last:pr-0">
          <div className={`text-4xl font-black leading-none tracking-[-0.04em] tabular-nums md:text-5xl ${dark ? "text-white" : "text-navy"}`}>{s.value}</div>
          <div className={`mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] ${dark ? "text-white/55" : "text-navy/55"}`}>{s.label}</div>
        </Reveal>
      ))}
    </div>
  );
}

export function MiniCaseCard({ slug, headlineOverride }) {
  const cs = caseBySlug(slug);
  if (!cs) return null;
  const stat = cs.heroStats && cs.heroStats.find((s) => !String(s.value).startsWith("[PLACEHOLDER"));
  return (
    <Link
      to={`${draftPath("/case-studies")}/${cs.slug}`}
      data-testid={`mini-case-${slug}`}
      className="group flex flex-col rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-7"
    >
      {cs.teamPhoto ? (
        <div className="relative -mx-6 -mt-6 mb-2 h-40 overflow-hidden rounded-t-xl">
          <img src={cs.teamPhoto.src} alt={cs.teamPhoto.alt || cs.client} loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-navy/80 to-transparent px-4 pb-3 pt-8">
            {cs.logoLight && <img src={cs.logoLight} alt="" className="h-5 w-auto" />}
            {cs.teamPhoto.caption && <span className="text-[11px] font-medium text-white/85">{cs.teamPhoto.caption}</span>}
          </div>
        </div>
      ) : (
        <LogoTile logo={cs.logo} name={cs.client} />
      )}
      {stat && (
        <div className="mt-5">
          <span className="text-3xl font-extrabold text-navy">{stat.value}</span>
          <div className="mt-1 text-xs font-medium uppercase tracking-widest text-navy/60">{stat.label}</div>
        </div>
      )}
      <p className="mt-4 flex-1 text-base leading-snug text-navy">{headlineOverride || cs.headline}</p>
      <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-link transition-all group-hover:gap-3">
        See the full case study <ArrowRight size={16} />
      </span>
    </Link>
  );
}

const CLIENT_TILES = ["dlg", "big-life-journal", "ink-volt", "talking-point-cards", "mindjournal", "zipify", "boom", "kajabi", "crumbl", "ella-mila", "gabby", "nutripaw", "arrae", "overtone", "satchel", "shopify"];

export function LogoBar() {
  const logos = [...CLIENT_TILES, ...CLIENT_TILES].map((n) => ({ name: n.replace(/-/g, " "), logo: `/assets/legacy/tile-${n}.webp` }));
  return (
    <Shell testId="logo-bar" className="!py-0">
      <div className="py-12">
        <div className="text-center text-[11px] font-extrabold uppercase tracking-widest text-navy/40">Brands we have run accounts for</div>
        <div className="relative mt-8 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div data-testid="logo-marquee" className="logo-marquee flex w-max items-center gap-5">
            {logos.map((c, i) => (
              <div key={`${c.name}-${i}`} className="flex h-[72px] w-[168px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white shadow-[0_4px_20px_-6px_rgba(7,0,93,0.10)] ring-1 ring-navy/[0.05]">
                <img src={c.logo} alt={c.name} loading="lazy" className="h-full w-full object-cover opacity-80 transition-opacity hover:opacity-100" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Shell>
  );
}

export function VideoTestimonials({ items = stats.videoTestimonials, textItems = stats.textTestimonials, title = "What clients say." }) {
  return (
    <Shell testId="testimonials">
      <H2>{title}</H2>
      <div className={`mt-10 grid gap-8 ${items.length % 3 === 0 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
        {items.map((t) => (
          <figure key={t.name} data-testid={`video-testimonial-${t.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <WistiaEmbed id={t.wistiaId} title={`${t.name}, ${t.brand}`} />
            <img src="/assets/legacy/5-stars.svg" alt="Five stars" className="mt-5 h-4 w-auto" />
            <blockquote className="mt-3 font-serif text-[19px] italic leading-[1.45] text-navy">"{t.quote}"</blockquote>
            <figcaption className="mt-3 text-sm text-navy/60"><span className="font-bold text-navy">{t.name}</span>, {t.brand}</figcaption>
          </figure>
        ))}
      </div>
      {textItems && textItems.length > 0 && (
        <div className={`mt-14 grid gap-6 ${textItems.length === 1 ? "" : textItems.length % 3 === 0 ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
          {textItems.map((t) => (
            <figure key={t.name} className="rounded-2xl bg-fog p-7 ring-1 ring-navy/[0.04]">
              <blockquote className="text-sm leading-relaxed text-navy">"{t.quote}"</blockquote>
              <figcaption className="mt-4 text-xs text-navy/60"><span className="font-bold text-navy">{t.name}</span>, {t.title}</figcaption>
            </figure>
          ))}
        </div>
      )}
    </Shell>
  );
}

export function WhoFor({ fit = stats.whoFor.fit, notFit = stats.whoFor.notFit, intro = stats.whoFor.intro, tone = "navy" }) {
  const light = tone === "navy";
  const Col = ({ title, items, good }) => (
    <Reveal delay={good ? 0 : 0.1} className={`rounded-2xl p-8 md:p-10 ${good ? "bg-white" : light ? "border border-white/15 bg-white/5" : "border border-mist bg-fog"}`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-full ${good ? "bg-green text-white" : "bg-pink text-white"}`}>
          {good ? <Check size={22} strokeWidth={3} /> : <X size={22} strokeWidth={3} />}
        </span>
        <h3 className={`text-2xl font-bold md:text-3xl ${good ? "text-navy" : light ? "text-white" : "text-navy"}`}>{title}</h3>
      </div>
      <ul className="mt-8 space-y-5">
        {items.map((f) => (
          <li key={f} className={`flex gap-4 text-lg leading-snug md:text-xl ${good ? "text-navy" : light ? "text-white/85" : "text-navy/80"}`}>
            {good ? <Check size={24} className="mt-0.5 shrink-0 text-green" strokeWidth={3} /> : <X size={24} className="mt-0.5 shrink-0 text-pink" strokeWidth={3} />}
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </Reveal>
  );
  return (
    <Shell tone={tone} testId="who-for">
      <div className="max-w-3xl">
        <H2 light={light}>{stats.whoFor.heading}</H2>
        <p className={`mt-5 text-lg leading-relaxed md:text-xl ${light ? "text-white/75" : "text-navy/75"}`}>{intro}</p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <Col title="A fit if" items={fit} good />
        <Col title="Not a fit if" items={notFit} good={false} />
      </div>
    </Shell>
  );
}

export function Steps({ lead, steps, tone = "fog", label = "Step" }) {
  return (
    <Shell tone={tone} testId="steps">
      {lead && <H2>{lead}</H2>}
      <div className="mt-10 grid gap-6 md:grid-cols-4">
        {steps.map((s, i) => (
          <Reveal key={i} delay={i * 0.08} className="rounded-2xl bg-white shadow-[0_4px_24px_-4px_rgba(7,0,93,0.06),0_1px_2px_rgba(7,0,93,0.04)] ring-1 ring-navy/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-8px_rgba(7,0,93,0.12)] p-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">{label} {i + 1}</div>
            <p className="mt-3 text-base leading-snug text-navy">{s}</p>
          </Reveal>
        ))}
      </div>
    </Shell>
  );
}
